import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { downloadPdf } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  Save,
  Send,
  History as HistoryIcon,
  Sparkles,
  ScrollText,
  Loader2,
  CheckCircle2,
  Wand2,
  Plus,
  Download,
} from "lucide-react";

const SECTIONS = [
  { key: "background", label: "Background", help: "Set up the agenda and global context. 80–150 words." },
  { key: "country_policy", label: "Country Policy", help: "Your assigned country's official stance with citations." },
  { key: "key_concerns", label: "Key Concerns", help: "What does your country worry about most on this agenda?" },
  { key: "proposed_solutions", label: "Proposed Solutions", help: "Concrete, actionable, country-aligned ideas." },
  { key: "international_cooperation", label: "International Cooperation", help: "Allies, blocs and multilateral angles." },
  { key: "references", label: "References", help: "Sources — UN docs, treaties, news, government statements." },
];

export default function PositionPaperBuilderPage() {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState({
    event_id: "",
    committee_id: "",
    country: "",
    title: "",
    sections: SECTIONS.reduce((a, s) => ({ ...a, [s.key]: "" }), {}),
    research_notes: "",
  });
  const [savedPaper, setSavedPaper] = useState(null); // { id, version, status, ... }
  const [events, setEvents] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [autoStatus, setAutoStatus] = useState("idle"); // idle | saving | saved
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const debounceRef = useRef();

  // Load existing paper if editing
  useEffect(() => {
    if (paperId) {
      api.get(`/papers/draft/${paperId}`).then((r) => {
        const p = r.data;
        setPaper({
          event_id: p.event_id,
          committee_id: p.committee_id,
          country: p.country || "",
          title: p.title || "",
          sections: { ...SECTIONS.reduce((a, s) => ({ ...a, [s.key]: "" }), {}), ...(p.sections || {}) },
          research_notes: p.research_notes || "",
        });
        setSavedPaper(p);
        setVersions(p.versions || []);
      }).catch(() => toast.error("Couldn't load paper"));
    } else {
      // Auto-fill from registration
      api.get("/registrations").then((r) => {
        const reg = r.data?.[0];
        if (reg) {
          setPaper((p) => ({
            ...p,
            event_id: reg.event_id,
            committee_id: reg.council_id || "",
            country: reg.country || "",
            title: reg.country ? `Position Paper — ${reg.country}` : "",
          }));
        }
      }).catch(() => {});
    }
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
  }, [paperId]);

  useEffect(() => {
    if (paper.event_id) {
      api.get(`/events/${paper.event_id}`).then((r) => {
        setCommittees(r.data?.councils || []);
      }).catch(() => {});
    }
  }, [paper.event_id]);

  // Auto-save (debounced) on any change after first manual save
  const saveDraft = useCallback(async (silent = false) => {
    if (!paper.event_id) {
      if (!silent) toast.error("Select an event first");
      return null;
    }
    setAutoStatus("saving");
    try {
      const { data } = await api.post("/papers/draft", paper);
      setSavedPaper(data);
      setAutoStatus("saved");
      if (!silent) toast.success(`Saved · v${data.version}`);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
      setAutoStatus("idle");
      return null;
    }
  }, [paper]);

  useEffect(() => {
    if (!savedPaper) return;
    clearTimeout(debounceRef.current);
    setAutoStatus("idle");
    debounceRef.current = setTimeout(() => saveDraft(true), 1800);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paper.sections, paper.research_notes, paper.title, paper.country]);

  const submit = async () => {
    const saved = savedPaper || (await saveDraft(true));
    if (!saved) return;
    try {
      await api.post("/papers/submit", { paper_id: saved.id });
      toast.success("Paper submitted to chair for review");
      const r = await api.get(`/papers/draft/${saved.id}`);
      setSavedPaper(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit");
    }
  };

  const fetchAi = async () => {
    const saved = savedPaper || (await saveDraft(true));
    if (!saved) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/papers/${saved.id}/clause-suggestions`);
      setAiSuggestions(data);
    } catch {
      toast.error("Couldn't fetch AI suggestions");
    } finally {
      setAiLoading(false);
    }
  };

  const submitted = savedPaper?.status === "submitted";

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        {/* Top bar */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Position Paper Builder
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              {savedPaper?.title || "New position paper"}
            </h1>
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500" data-testid="paper-status">
              {savedPaper && (
                <>
                  <span className="font-mono">v{savedPaper.version}</span>
                  <span>·</span>
                  <StatusPill status={savedPaper.status} review={savedPaper.chair_review_status} />
                </>
              )}
              <span className="inline-flex items-center gap-1.5">
                {autoStatus === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
                {autoStatus === "saved" && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                {autoStatus === "idle" && savedPaper && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
                <span>
                  {autoStatus === "saving"
                    ? "Saving…"
                    : autoStatus === "saved"
                    ? "Auto-saved"
                    : savedPaper
                    ? "Edits pending"
                    : "Not saved"}
                </span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/dashboard/papers" className="text-xs text-slate-600 hover:text-[#0F4C81] underline-offset-2 hover:underline">
              All my papers
            </Link>
            <Link to="/dashboard/ai-prep">
              <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white" data-testid="open-prep-button">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Open AI Prep
              </Button>
            </Link>
            <Button
              onClick={() => saveDraft(false)}
              variant="outline"
              className="border-slate-300"
              data-testid="paper-save-button"
            >
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save
            </Button>
            <Button
              onClick={async () => {
                if (!paper.id) {
                  toast.error("Save the draft first to download a PDF.");
                  return;
                }
                try {
                  await downloadPdf(`/papers/${paper.id}/pdf`);
                  toast.success("Position paper PDF downloaded");
                } catch {
                  toast.error("Could not generate PDF.");
                }
              }}
              variant="outline"
              className="border-[#0F4C81] text-[#0F4C81]"
              data-testid="paper-pdf-button"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
            </Button>
            <Button
              onClick={submit}
              disabled={submitted}
              className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
              data-testid="paper-submit-button"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> {submitted ? "Submitted" : "Submit"}
            </Button>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          {/* Editor */}
          <div className="lg:col-span-8 space-y-5">
            {/* Header card */}
            <div className="rounded-2xl bg-white border border-slate-200 p-6 grid sm:grid-cols-3 gap-4" data-testid="paper-header-form">
              <div className="sm:col-span-3">
                <Label className="text-xs">Title</Label>
                <Input value={paper.title} onChange={(e) => setPaper({ ...paper, title: e.target.value })} data-testid="paper-title" />
              </div>
              <div>
                <Label className="text-xs">Event</Label>
                <select
                  value={paper.event_id}
                  onChange={(e) => setPaper({ ...paper, event_id: e.target.value })}
                  className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="paper-event"
                >
                  <option value="">Select event…</option>
                  {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Committee</Label>
                <select
                  value={paper.committee_id || ""}
                  onChange={(e) => setPaper({ ...paper, committee_id: e.target.value })}
                  className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="paper-committee"
                >
                  <option value="">Select committee…</option>
                  {committees.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Country</Label>
                <Input value={paper.country} onChange={(e) => setPaper({ ...paper, country: e.target.value })} data-testid="paper-country" />
              </div>
            </div>

            {/* Sections */}
            {SECTIONS.map((s) => (
              <div key={s.key} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`section-${s.key}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                      Section
                    </p>
                    <h3 className="mt-1 text-lg font-semibold tracking-tight text-[#0A192F]">{s.label}</h3>
                  </div>
                  <span className="text-[10px] text-slate-400">{(paper.sections[s.key] || "").length} chars</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{s.help}</p>
                <Textarea
                  rows={6}
                  value={paper.sections[s.key]}
                  onChange={(e) => setPaper({ ...paper, sections: { ...paper.sections, [s.key]: e.target.value } })}
                  placeholder={`Write your ${s.label.toLowerCase()} here…`}
                  className="mt-3"
                  data-testid={`paper-${s.key}`}
                />
              </div>
            ))}

            {/* Research notes */}
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Research notes (private)
              </p>
              <h3 className="mt-1 text-lg font-semibold tracking-tight text-[#0A192F]">Scratchpad</h3>
              <p className="mt-1 text-xs text-slate-500">Visible only to you. Use for sources, links, half-formed thoughts.</p>
              <Textarea
                rows={5}
                value={paper.research_notes}
                onChange={(e) => setPaper({ ...paper, research_notes: e.target.value })}
                placeholder="Drop links, quotes, source notes, half-baked thoughts…"
                className="mt-3"
                data-testid="paper-research-notes"
              />
            </div>
          </div>

          {/* Right rail: AI clause helper + version history */}
          <div className="lg:col-span-4 space-y-5">
            <div className="rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-white p-6 border border-[#D4AF37]/20">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-md bg-[#D4AF37] text-[#0A192F] flex items-center justify-center">
                  <Wand2 className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Diplomexa AI</p>
                  <p className="text-base font-semibold tracking-tight">Clause helper</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-300">
                Get instant strengths, gaps and sample clauses from the Diplomexa AI engine.
              </p>
              <Button
                onClick={fetchAi}
                disabled={aiLoading || !paper.country || Object.values(paper.sections).every((v) => !v)}
                className="mt-4 w-full bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
                data-testid="paper-ai-suggestions-button"
              >
                {aiLoading ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Analysing…</> : "Get AI suggestions"}
              </Button>
              {aiSuggestions && (
                <div className="mt-5 space-y-4 text-sm" data-testid="paper-ai-suggestions">
                  {aiSuggestions.strengths?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Strengths</p>
                      <ul className="mt-1.5 space-y-1 text-slate-100 text-xs">
                        {aiSuggestions.strengths.map((x, i) => <li key={i}>· {x}</li>)}
                      </ul>
                    </div>
                  )}
                  {aiSuggestions.gaps?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">Gaps</p>
                      <ul className="mt-1.5 space-y-1 text-slate-100 text-xs">
                        {aiSuggestions.gaps.map((x, i) => <li key={i}>· {x}</li>)}
                      </ul>
                    </div>
                  )}
                  {aiSuggestions.suggestions?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Suggestions</p>
                      <ul className="mt-1.5 space-y-1 text-slate-100 text-xs">
                        {aiSuggestions.suggestions.map((x, i) => <li key={i}>· {x}</li>)}
                      </ul>
                    </div>
                  )}
                  {aiSuggestions.sample_clauses?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Sample clauses (copy & adapt)</p>
                      <div className="mt-1.5 space-y-2">
                        {aiSuggestions.sample_clauses.map((x, i) => (
                          <div key={i} className="rounded-md bg-white/10 p-2.5 text-xs italic text-slate-100">
                            "{x}"
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Version history */}
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="paper-versions">
              <div className="flex items-center gap-2">
                <HistoryIcon className="h-4 w-4 text-[#0F4C81]" />
                <p className="text-sm font-semibold text-[#0A192F]">Version history</p>
              </div>
              <p className="mt-1 text-xs text-slate-500">Auto-snapshots on every save.</p>
              {(versions || []).length === 0 ? (
                <p className="mt-4 text-xs text-slate-400">No previous versions yet.</p>
              ) : (
                <ul className="mt-4 space-y-2 max-h-80 overflow-y-auto">
                  {versions.map((v) => (
                    <li key={v.id} className="text-xs flex items-center justify-between p-2 rounded-md bg-slate-50">
                      <span className="font-mono text-slate-700">v{v.version}</span>
                      <span className="text-slate-500">{v.saved_at ? new Date(v.saved_at).toLocaleString() : "—"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatusPill({ status, review }) {
  const map = {
    draft: { color: "bg-slate-100 text-slate-700", label: "Draft" },
    submitted: { color: "bg-emerald-100 text-emerald-700", label: review === "pending" ? "Submitted · Awaiting review" : "Submitted" },
  };
  const m = map[status] || map.draft;
  return <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${m.color}`}>{m.label}</span>;
}
